import * as React from 'react';
import { List } from 'immutable';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { debounceCancelable } from 'lib/helpers';
import { documentDocumentIdSelector } from 'domain/documents';
import { RecordCellSet } from 'domain/journal/helper';
import elements from 'components/elements';
import {
  valueNormalize as defaultValueNormalize,
  filterList as defaultFilterList,
  testOption,
} from 'components/Tables/Cells/ListCell/helpers';

import {
  getJEListAction,
  paginationListSelector,
  paginationListPageTokenSelector,
  getJEIndexListItemDataAction,
  getCachedPaginationListSelector,
  updateCurrentPaginationListAction,
} from 'domain/journal';

import withManageListItemModal from 'components/Tables/Cells/ListCell/withManageListItemModal';
import AutocompleteOptions from 'components/Tables/Cells/ListCell/options';
import Tooltip from 'components/mui/Tooltip';
import {
  FieldInput,
  FieldWarningIcon,
  FieldWrapper,
  ListCellWrapper,
} from 'components/Tables/Cells/ListCell/StyledComponents';
import { CellToggleButton } from 'components/Tables/Cells/Cell/StyledComponents';

import WarningIcon from '@mui/icons-material/Warning';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

const QUERY_PAGE_SIZE = 50;
export const QUERY_ALL_PAGES = -1;
const MAX_ITEMS_DISPLAY = 10000;

class Autocomplete extends React.Component {
  // TODO: IMPORTANT !!!!!!
  // TODO: all component is very fragile/delicate, be careful when you change the order of any flags and the like

  debounceGetOptionListFn = debounceCancelable(250, (isNewList, search = null, cb = () => {}) => {
    this.loadOptionList(isNewList, search).then((res) => cb?.(res));
  });

  constructor(props) {
    super(props);

    const { optionList } = props;

    this.state = {
      term: '',
      index: -1,
      isDirectionUP: undefined, // eslint-disable-line react/no-unused-state
      isLoading: false,
      currentOptionsList: optionList,
      selectedValues: this.parseInitialSelectedValues(props),
    };
  }

  parseInitialSelectedValues(props) {
    const { input } = props;
    const currentValue = input?.value?.value;
    if (!currentValue) return new Set();
    // Values may be comma-separated from previous multiselect
    return new Set(
      String(currentValue)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
    );
  }

  componentDidMount() {
    this.getOptionList(true);
  }

  componentWillReceiveProps(nextProps) {
    const { optionList } = this.props;

    if (optionList.size !== nextProps.optionList.size) {
      this.setState({ currentOptionsList: nextProps.optionList });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isLoading, isPressSelectOnLoadingEnd } = this.state;
    const { optionList } = this.props;

    // IF was pressed enter or tab when options were loading, check this case
    // if search result is one item and enter/tab was pressed, select and move to next cell
    if (isLoading === false && prevState.isLoading === true && optionList.size === 1 && isPressSelectOnLoadingEnd) {
      this.doSelect(0, this.cbEnter);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    this.debounceGetOptionListFn.cancel();
  }

  // eslint-disable-next-line getter-return
  get cachedList() {
    const { getCachedPaginationList, include_id } = this.props;
    if (include_id) {
      return getCachedPaginationList(include_id);
    }
  }

  // this need for isLoading flags etc, state flags must change immediately
  getOptionList = (isNewList) => {
    const { cachedList, props, startLoader, debounceGetOptionListFn } = this;
    const { updateCurrentPaginationList } = props;

    if (!cachedList) {
      startLoader(isNewList);
      debounceGetOptionListFn(isNewList);
    } else {
      updateCurrentPaginationList({ list: cachedList });
    }
  };

  onBlur = () => {
    const { input, blurTimeOut, onComplete } = this.props;
    const { index } = this.state;
    this.focused = false;
    this.timeout = setTimeout(() => {
      if (!this.focused) {
        if (this.props.multiSelect) {
          this.applyMultiSelect(onComplete);
        } else if (index && index >= 1) {
          this.doSelect(index);
        }
        this.handleClose();
        input.onBlur();
      }
    }, blurTimeOut);
  };

  onFocus = () => {
    this.focused = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  };

  onCreate = (cb) => {
    const { onCreate, input } = this.props;

    onCreate()
      .then((options) => {
        if (options) {
          const set = RecordCellSet({ value: '', options });
          input.onCreate(set, cb);
        }
      })
      .catch(() => {
        if (this.input) this.input.focus();
        this.slected = false;
      });
  };

  onEdit = (optionItem, cb) => {
    const { onEdit, input } = this.props;

    onEdit(optionItem.get('value'))
      .then((options) => {
        const set = RecordCellSet({
          value: optionItem.get('value'),
          display: optionItem.get('display'),
          options,
        });
        input.onCreate(set, cb);
      })
      .catch(() => {
        if (this.input) this.input.focus();
        this.slected = false;
      });
  };

  onDropCreate = async (dndValue) => {
    const cb = async () => {
      const { options } = this;
      const testDndValue = testOption(dndValue);
      const list = options.filter(testDndValue(false));
      const results = options.findIndex(testDndValue(true));

      if (this.isCreateAvailable) {
        if (results < 0) {
          // if there is no exact match, we show list of submatches found,
          // or open create dialog if mo matches
          if (list.size > 0) {
            await this.setValue(dndValue, 0);
            this.handleOpen();
          } else {
            await this.setValue(dndValue);
            this.onCreate(this.cbEnter);
          }
          // this.props.input.onToggleOpen(true);
        } else if (list.size === 1) {
          this.doSelect(results, this.cbEnter);
        } else {
          await this.setValue(dndValue, 0);
          this.handleOpen();
        }
      } else {
        this.setValue(dndValue, 0).then(() => {
          this.getOptionList(true);
          this.handleOpen();
        });
      }
    };

    this.debounceGetOptionListFn(true, dndValue, cb);
  };

  onChange = (value, cb = (x) => x) => {
    const { options, props } = this;
    const {
      input: { onChange },
    } = props;
    const item = options.filter((f) => f.value === value).first();
    const { display } = item || { display: '' };
    onChange({ target: { value, display } }, cb);
  };

  toggleMultiSelectValue = (item) => {
    this.setState((prevState) => {
      const newSelected = new Set(prevState.selectedValues);
      if (newSelected.has(item.value)) {
        newSelected.delete(item.value);
      } else {
        newSelected.add(item.value);
      }
      return { selectedValues: newSelected };
    });
  };

  applyMultiSelect = (cb) => {
    const { selectedValues } = this.state;

    const selectedItems = Array.from(selectedValues)
      .map(value => this.options.find(opt => opt.value === value))
      .filter(Boolean);

    const commaSeparatedValue = selectedItems.map(item => item.value).join(', ');
    const commaSeparatedDisplay = selectedItems.map(item => item.display).join(', ');

    const { onChange } = this.props.input;
    onChange({ target: { value: commaSeparatedValue, display: commaSeparatedDisplay } }, cb);
  };

  getFullOptionList = () => {
    const { optionList } = this.props;
    return this.cachedList || optionList;
  };

  onRenderItems = (from, to) => {
    this.fromItem = from;
    this.toItem = to;
    const { options } = this;
    const { isLoading } = this.state;
    const { nextPageToken } = this.props;
    const { size } = options;
    // todo change after back fix
    const queryPoint = size - QUERY_PAGE_SIZE / 4;

    // todo change after back fix
    if (MAX_ITEMS_DISPLAY >= size && size >= QUERY_PAGE_SIZE / 2 && nextPageToken && to > queryPoint && !isLoading) {
      this.loadOptionList().then();
    }
  };

  get term() {
    const { options } = this;
    const { input } = this.props;
    if (input.value.value) {
      const value = options.filter((f) => f.id === input.value.value).first();
      this.slected = true;
      if (typeof value === 'string') return this.props.valueNormalize(value);
    }
    return '';
  }

  get value() {
    const {
      state: { term },
    } = this;
    return term || this.term;
  }

  get isCreateAvailable() {
    return this.props.input.isCreatable;
  }

  get options() {
    const { term } = this.state;
    const { optionList } = this.props;
    return term && this.cachedList ? this.filterSearchList(optionList, term) : optionList;
  }

  get currentOptions() {
    const { term, currentOptionsList, selectedValues } = this.state;
    const list = term && this.cachedList ? this.filterSearchList(currentOptionsList, term) : currentOptionsList;
    // Mark selected items so react-window re-renders rows when selection changes
    if (this.props.multiSelect && selectedValues.size > 0) {
      return list.map((item) => (item ? item.set('selected', selectedValues.has(item.value)) : item));
    }
    return list;
  }

  getSearchQuery = (isNewList, search) => {
    const { term } = this.state;
    const {
      name: { name },
      nextPageToken,
      include_id,
    } = this.props;

    const queryForAllPages = {
      search: '',
      pageSize: QUERY_ALL_PAGES,
      pageToken: null,
    };

    return {
      search: term || search,
      pageSize: QUERY_PAGE_SIZE,
      cellName: name,
      pageToken: isNewList ? null : nextPageToken,
      ...(include_id && queryForAllPages),
    };
  };

  setValue = (term, index = -1) =>
    new Promise((resolve) => {
      this.setState({ term, index }, () => {
        resolve();
      });
    });

  setActive = (index) => {
    if (this.state.index !== index) {
      this.setState({ index }, () => {
        if (index >= 0) {
          this.scrollTo(index);
        }
      });
    }
  };

  setDirection = (isUp) => {
    this.setState({
      isDirectionUP: isUp, // eslint-disable-line react/no-unused-state
    });
  };

  keyDown = (e) => {
    const { options: optionList } = this;
    const { index, isLoading } = this.state;
    const { input } = this.props;

    switch (e.keyCode) {
      case 38: {
        // up arrow
        const overlapIndex = optionList.size - 1;
        this.setActive(index > 0 ? index - 1 : overlapIndex);
        break;
      }
      case 40: {
        // down arrow
        const overlapIndex = 0;
        this.setActive(optionList.size > index + 1 ? index + 1 : overlapIndex);
        break;
      }
      case 27: {
        // escape
        if (this.props.multiSelect) {
          // Discard changes, restore original values
          this.setState({ selectedValues: this.parseInitialSelectedValues(this.props) });
        }
        this.handleClose();
        input.onKeyDown({ keyCode: 27 });
        break;
      }
      case 8: {
        // backspace
        if (this.slected) {
          if (this.props.multiSelect) {
            this.setState({ selectedValues: new Set() });
          }
          this.onChange('');
          this.slected = false;
        }
        break;
      }
      case 9:
      case 13: {
        // TAB / enter
        e.preventDefault();

        if (isLoading) {
          this.setState({ isPressSelectOnLoadingEnd: true });
        } else if (this.props.multiSelect) {
          this.applyMultiSelect(this.cbEnter);
          this.handleClose();
          this.slected = true;
          this.setState({ isPressSelectOnLoadingEnd: false });
        } else {
          this.doSelect(index, this.cbEnter);
          this.slected = true;
          this.setState({ isPressSelectOnLoadingEnd: false });
        }

        break;
      }
      // no default
    }
  };

  filterSearchList = (data, term) => {
    const testValue = testOption(term);
    return data.filter(testValue(false));
  };

  doSelect = (index, cb) => {
    const { options: optionList } = this;
    const { isLoading } = this.state;
    const item = optionList.get(index);

    if ((item && !item.get('isActive')) || isLoading) return;

    if (index === -1) {
      if (!this.props.multiSelect) {
        this.onChange('', cb);
      }
      return;
    }

    if (item) {
      if (this.props.multiSelect) {
        // Toggle the value in multiselect - dropdown stays open
        this.toggleMultiSelectValue(item);
        // If cb is provided (e.g. from componentDidUpdate auto-select), apply immediately
        if (cb) {
          this.applyMultiSelect(cb);
          this.handleClose();
          this.slected = true;
          this.setState({ isPressSelectOnLoadingEnd: false });
        }
      } else {
        // Single-select: select and close
        this.onChange(item.get('value'), cb);
        this.handleClose();
        this.slected = true;
        this.setState({ isPressSelectOnLoadingEnd: false });
      }
    }
  };

  cbEnter = () => this.props.input.onKeyDown({ keyCode: 13 });

  refsProxy = (el) => {
    this.input = el;
    this.props.getRefs(el, this);
  };

  searchHandler = (e, isLoading, idx, cb = () => {}) => {
    const { value } = e.currentTarget;
    this.handleOpen();
    // if we have search results to display set index to 0. 0 means we select first from a list
    // if no search results, -1 must remain caz it allows for onCreate to be invoked
    // on enter key event
    const index = idx !== 'undefined' ? idx : this.options.size ? 0 : -1;
    this.setState({ term: value, index, ...(isLoading !== 'undefined' && { isLoading }) }, cb);
  };

  asyncSearchHandler = (e) => {
    this.searchHandler(e, true, 0, this.getOptionList(true));
  };

  onSearchChanged = (e) => {
    const { cachedList, searchHandler, asyncSearchHandler } = this;
    const handler = cachedList ? searchHandler : asyncSearchHandler;
    handler(e);
  };

  scrollTo = (index) => {
    const shiftVLIndex = this.isCreateAvailable ? index + 1 : index;
    if (this.listRef && this.listRef.current) {
      this.listRef.current.scrollToItem(shiftVLIndex, 'smart');
    }
  };

  startLoader = (isNewList) => {
    const { currentOptionsList } = this.state;
    this.setState({ isLoading: true, currentOptionsList: isNewList ? new List() : currentOptionsList });
  };

  loadOptionList = (isNewList = false, search = null) => {
    const { term } = this.state;
    const { getJEList, include_id } = this.props;
    this.startLoader(isNewList);
    if (term !== null || search !== null) {
      return new Promise((resolve, reject) => {
        getJEList({ query: this.getSearchQuery(isNewList, search), isNewList, resolve, reject, include_id });
      }).finally(() => this.setState({ isLoading: false }));
    }
  };

  handleClose = () => {
    const { input } = this.props;

    input.onToggleOpen(false);
  };

  handleOpen = () => {
    const { input } = this.props;

    input.onToggleOpen(true);
  };

  handleToggle = () => {
    const { isOpen } = this.props;
    const fn = isOpen ? this.handleClose : this.handleOpen;
    fn();
  };

  slected = false;

  input;

  focused;

  timeout;

  listRef = React.createRef();

  fromItem = 0;

  toItem = 0;

  render() {
    const {
      style,
      name,
      pageItemsCount,
      isOpen,
      id,
      placeholder,
      disabled,
      maxWidth,
      rtl,
      onComplete,
      valueNormalize,
      children,
    } = this.props;
    const { isLoading, index, term } = this.state;

    const isVisibleIcon = term && this.options.size === 0 && !isLoading;

    return (
      <ListCellWrapper
        tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyDown={this.keyDown}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={id}
        data-element={elements.je.autocomplete.container}
        data-element-id={name.name}
      >
        <FieldWrapper>
          {isVisibleIcon && (
            <FieldWarningIcon>
              <Tooltip
                t={{
                  id: 'tooltip.je.cell.paginated.empty.attention',
                  defaultMessage: 'No results found matching search criteria',
                }}
              >
                <WarningIcon color="error" />
              </Tooltip>
            </FieldWarningIcon>
          )}
          <FieldInput
            id={id}
            aria-autocomplete="list"
            autoComplete="no"
            placeholder={placeholder}
            name={name._cell}
            value={this.value}
            disabled={disabled}
            ref={this.refsProxy}
            onChange={this.onSearchChanged}
            onDoubleClick={() => this.handleOpen()}
            style={style}
            data-element={elements.je.autocomplete.field}
            data-element-id={`${name._cell}-${name.name}`}
            {...(isVisibleIcon ? { sx: { paddingLeft: '18px' } } : {})}
          />
        </FieldWrapper>
        {isOpen && this.input ? (
          <AutocompleteOptions
            inputEl={this.input}
            maxWidth={maxWidth}
            rtl={rtl}
            optionList={this.currentOptions}
            doSelect={(e) => this.doSelect(e)}
            valueNormalize={valueNormalize}
            index={index}
            term={term}
            setDirection={this.setDirection}
            onRenderItems={this.onRenderItems}
            isLoading={isLoading}
            pageItemsCount={pageItemsCount}
            itemHeight={30}
            setListRef={this.listRef}
            setActive={this.setActive}
            onClickEdit={(item) => this.onEdit(item, this.cbEnter)}
            addNewItem={this.isCreateAvailable ? () => this.onCreate(this.cbEnter) : null}
            multiSelect={this.props.multiSelect}
          />
        ) : null}
        <CellToggleButton onClick={this.handleToggle} data-element={elements.je.autocomplete.shield}>
          {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </CellToggleButton>
        {children(this.value, this.searchHandler)}
      </ListCellWrapper>
    );
  }
}

Autocomplete.defaultProps = {
  valueNormalize: defaultValueNormalize,
  filterList: defaultFilterList,
  blurTimeOut: 100,
};

const mapStateToProps = (state) => ({
  optionList: paginationListSelector(state),
  nextPageToken: paginationListPageTokenSelector(state),
  documentID: documentDocumentIdSelector(state),
  getCachedPaginationList: getCachedPaginationListSelector(state),
});

const mapDispatchToProps = {
  getJEList: getJEListAction,
  getIndexForm: getJEIndexListItemDataAction,
  updateCurrentPaginationList: updateCurrentPaginationListAction,
};

export { Autocomplete as AC };

export default compose(connect(mapStateToProps, mapDispatchToProps), withManageListItemModal)(Autocomplete);
